import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { BanknotesIcon, CreditCardIcon, BuildingLibraryIcon, EyeIcon, EyeSlashIcon, CheckIcon } from '@heroicons/react/24/outline'
import { storePaymentsAPI } from '../services/api'
import { t } from '../i18n'

// Settings > Payments: which payment methods this store's customers can use
// at checkout. Saved separately from the other settings because it holds the
// store's Stripe secret key, which the server stores encrypted and never
// sends back.
const EMPTY = {
  cash: { enabled: true },
  card: { enabled: false, publishableKey: '', secretKey: '', hasSecretKey: false, secretKeyHint: '' },
  bank_transfer: { enabled: false, accountName: '', bankName: '', accountNumber: '', iban: '', swift: '', instructions: '' },
}

function Toggle({ checked, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${checked ? 'bg-emerald-600' : 'bg-gray-300'}`}>
      <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  )
}

function MethodCard({ icon: Icon, title, description, enabled, onToggle, children }) {
  return (
    <section className={`rounded-xl border ${enabled ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start gap-4 p-5">
        <span className={`w-10 h-10 rounded-lg grid place-items-center flex-shrink-0 ${enabled ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'}`}><Icon className="h-5 w-5" /></span>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900">{t(title)}</h3>
          <p className="text-sm text-gray-600 mt-0.5">{t(description)}</p>
        </div>
        <Toggle checked={enabled} onChange={onToggle} label={t("Accept {title}", { title: title })} />
      </div>
      {enabled && children && <div className="border-t border-gray-200 p-5 space-y-4">{children}</div>}
    </section>
  )
}

export default function PaymentMethodsPanel() {
  const [config, setConfig] = useState(EMPTY)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showSecret, setShowSecret] = useState(false)

  const load = data => setConfig({
    cash: { ...EMPTY.cash, ...data.cash },
    card: { ...EMPTY.card, ...data.card, secretKey: '' },
    bank_transfer: { ...EMPTY.bank_transfer, ...data.bank_transfer },
  })

  useEffect(() => {
    storePaymentsAPI.getSettings()
      .then(({ data }) => load(data))
      .catch(err => toast.error(t(err.response?.data?.error || 'Could not load payment settings')))
      .finally(() => setLoading(false))
  }, [])

  const set = (method, patch) => setConfig(c => ({ ...c, [method]: { ...c[method], ...patch } }))
  const field = (method, key, label, props = {}) => (
    <div>
      <label className="form-label" htmlFor={`pay-${method}-${key}`}>{t(label)}</label>
      <input id={`pay-${method}-${key}`} className="form-input" value={config[method][key] || ''} onChange={e => set(method, { [key]: e.target.value })} {...props} />
    </div>
  )

  const save = async () => {
    setSaving(true)
    try {
      const { data } = await storePaymentsAPI.saveSettings(config)
      load(data)
      toast.success(t("Payment methods saved"))
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Could not save payment methods'))
    } finally { setSaving(false) }
  }

  if (loading) return <div className="card"><div className="card-body text-gray-500">{t("Loading payment methods...")}</div></div>

  const testMode = config.card.publishableKey.startsWith('pk_test_')
  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t("Customer payments")}</h2>
          <p className="text-gray-600">{t("Choose how customers can pay when they order from your website.")}</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center whitespace-nowrap"><CheckIcon className="h-5 w-5 me-2" />{saving ? t("Saving...") : t("Save payment methods")}</button>
      </div>
      <div className="card-body space-y-4">
        <MethodCard icon={BanknotesIcon} title={t("Cash")} description={t("Customers pay in person - at the counter, at pickup or on delivery.")}
          enabled={config.cash.enabled} onToggle={v => set('cash', { enabled: v })} />

        <MethodCard icon={CreditCardIcon} title={t("Card (Stripe)")} description={t("Customers pay online by card on a secure Stripe page. Money goes straight to your Stripe account.")}
          enabled={config.card.enabled} onToggle={v => set('card', { enabled: v })}>
          <p className="text-sm text-gray-600">{t("Find your keys in the Stripe Dashboard under")} <strong>{t("Developers → API keys")}</strong>{t(". Use test keys (pk_test_ / sk_test_) to try it first.")}</p>
          <div className="grid md:grid-cols-2 gap-4">
            {field('card', 'publishableKey', 'Publishable key', { placeholder: 'pk_live_...', autoComplete: 'off', spellCheck: false })}
            <div>
              <label className="form-label" htmlFor="pay-card-secret">{t("Secret key")}</label>
              <div className="relative">
                <input id="pay-card-secret" type={showSecret ? 'text' : 'password'} className="form-input pe-10" autoComplete="new-password" spellCheck={false}
                  value={config.card.secretKey} onChange={e => set('card', { secretKey: e.target.value, clearSecretKey: false })}
                  placeholder={config.card.hasSecretKey ? t("Saved ({secretKeyHint}) - leave blank to keep", { secretKeyHint: config.card.secretKeyHint }) : t("sk_live_...")} />
                <button type="button" onClick={() => setShowSecret(s => !s)} className="absolute inset-y-0 end-0 pe-3 flex items-center" aria-label={showSecret ? t("Hide secret key") : t("Show secret key")}>
                  {showSecret ? <EyeSlashIcon className="h-5 w-5 text-gray-400" /> : <EyeIcon className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t("Stored encrypted. It is never shown again or sent to customers.")}</p>
            </div>
          </div>
          {testMode && <p className="text-sm rounded-lg bg-amber-50 text-amber-800 px-3 py-2">{t("Test mode: no real money is charged. Use card 4242 4242 4242 4242 to try a payment.")}</p>}
        </MethodCard>

        <MethodCard icon={BuildingLibraryIcon} title={t("Bank transfer")} description={t("Customers see your bank details, make the transfer, then upload a photo of the receipt. You approve it on the Orders page.")}
          enabled={config.bank_transfer.enabled} onToggle={v => set('bank_transfer', { enabled: v })}>
          <div className="grid md:grid-cols-2 gap-4">
            {field('bank_transfer', 'accountName', 'Account holder name', { placeholder: 'Your business legal name', maxLength: 120 })}
            {field('bank_transfer', 'bankName', 'Bank name', { placeholder: 'e.g. Attijariwafa Bank', maxLength: 120 })}
            {field('bank_transfer', 'accountNumber', 'Account number / RIB', { maxLength: 64, spellCheck: false })}
            {field('bank_transfer', 'iban', 'IBAN', { maxLength: 64, spellCheck: false })}
            {field('bank_transfer', 'swift', 'SWIFT / BIC (optional)', { maxLength: 20, spellCheck: false })}
          </div>
          <div>
            <label className="form-label" htmlFor="pay-bank-instructions">{t("Instructions for customers (optional)")}</label>
            <textarea id="pay-bank-instructions" rows={2} maxLength={500} className="form-input" value={config.bank_transfer.instructions}
              onChange={e => set('bank_transfer', { instructions: e.target.value })} placeholder={t("Please use your order code as the transfer reference.")} />
          </div>
        </MethodCard>
      </div>
    </div>
  )
}
